/**
 * API client para a área admin do chat de suporte.
 * Requer autenticação (Bearer token).
 */

function authHeaders() {
  const token = localStorage.getItem('token')
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(`Erro ${res.status}: ${text}`)
  }
  return res.json()
}

// ─── Types ───────────────────────────────────────────────────────────────────

export interface ConversationListItem {
  id: string
  session_id: string
  user_email: string | null
  status: string
  message_count: number
  first_message: string | null
  created_at: string
  updated_at: string
}

export interface MessageItem {
  id: string
  role: 'user' | 'assistant'
  content: string
  created_at: string
}

export interface ConversationDetail extends ConversationListItem {
  messages: MessageItem[]
}

export interface ConversationListResponse {
  items: ConversationListItem[]
  total: number
  page: number
  per_page: number
}

export interface KbArticle {
  id: string
  title: string
  content: string
  source_url: string | null
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface KbFile {
  id: string
  file_name: string
  file_type: string | null
  file_size: number | null
  is_active: boolean
  has_content: boolean
  created_at: string
}

export interface Pipeline {
  id: string
  name: string
  display_name: string
  description: string | null
  is_enabled: boolean
  config: Record<string, string>
  updated_at: string
}

export interface PipelineRun {
  id: string
  pipeline_name: string
  conversation_id: string | null
  status: string
  trigger_data: Record<string, unknown> | null
  result_data: Record<string, unknown> | null
  error_message: string | null
  created_at: string
}

export interface UnansweredQuestion {
  id: string
  session_id: string
  question: string
  response_given: string | null
  is_resolved: boolean
  notes: string | null
  created_at: string
}

export interface Lead {
  id: string
  email: string
  name: string | null
  session_id: string | null
  source: string
  created_at: string
  converted_at: string | null
}

export interface PagedResponse<T> {
  items: T[]
  total: number
  page: number
  per_page: number
}

// ─── Conversations ────────────────────────────────────────────────────────────

export async function fetchConversations(params?: {
  page?: number
  status?: string
  email?: string
}): Promise<ConversationListResponse> {
  const query = new URLSearchParams()
  if (params?.page) query.set('page', String(params.page))
  if (params?.status) query.set('status', params.status)
  if (params?.email) query.set('email', params.email)
  const qs = query.toString()
  return handleResponse(
    await fetch(`/api/admin/support/conversations${qs ? `?${qs}` : ''}`, {
      headers: authHeaders(),
    })
  )
}

export async function fetchConversation(id: string): Promise<ConversationDetail> {
  return handleResponse(
    await fetch(`/api/admin/support/conversations/${id}`, { headers: authHeaders() })
  )
}

export async function deleteConversation(id: string): Promise<void> {
  await handleResponse(
    await fetch(`/api/admin/support/conversations/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    })
  )
}

export async function closeConversation(id: string): Promise<ConversationListItem> {
  return handleResponse(
    await fetch(`/api/admin/support/conversations/${id}/close`, {
      method: 'PATCH',
      headers: authHeaders(),
    })
  )
}

// ─── KB Articles ─────────────────────────────────────────────────────────────

export async function fetchKbArticles(): Promise<KbArticle[]> {
  return handleResponse(
    await fetch('/api/admin/support/kb/articles', { headers: authHeaders() })
  )
}

export async function createKbArticle(data: {
  title: string
  content: string
  source_url?: string
}): Promise<KbArticle> {
  return handleResponse(
    await fetch('/api/admin/support/kb/articles', {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(data),
    })
  )
}

export async function updateKbArticle(
  id: string,
  data: { title?: string; content?: string; source_url?: string }
): Promise<KbArticle> {
  return handleResponse(
    await fetch(`/api/admin/support/kb/articles/${id}`, {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify(data),
    })
  )
}

export async function deleteKbArticle(id: string): Promise<void> {
  await handleResponse(
    await fetch(`/api/admin/support/kb/articles/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    })
  )
}

export async function fetchUrlContent(url: string): Promise<{ title: string; content: string }> {
  return handleResponse(
    await fetch('/api/admin/support/kb/articles/fetch-url', {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ url }),
    })
  )
}

// ─── KB Files ─────────────────────────────────────────────────────────────────

export async function fetchKbFiles(): Promise<KbFile[]> {
  return handleResponse(
    await fetch('/api/admin/support/kb/files', { headers: authHeaders() })
  )
}

export async function uploadKbFile(file: File): Promise<KbFile> {
  const form = new FormData()
  form.append('file', file)
  const token = localStorage.getItem('token')
  return handleResponse(
    await fetch('/api/admin/support/kb/files', {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    })
  )
}

export async function deleteKbFile(id: string): Promise<void> {
  await handleResponse(
    await fetch(`/api/admin/support/kb/files/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    })
  )
}

// ─── Pipelines ────────────────────────────────────────────────────────────────

export async function fetchPipelines(): Promise<Pipeline[]> {
  return handleResponse(
    await fetch('/api/admin/support/pipelines', { headers: authHeaders() })
  )
}

export async function togglePipeline(name: string): Promise<Pipeline> {
  return handleResponse(
    await fetch(`/api/admin/support/pipelines/${name}/toggle`, {
      method: 'PATCH',
      headers: authHeaders(),
    })
  )
}

export async function updatePipelineConfig(
  name: string,
  config: Record<string, string>
): Promise<Pipeline> {
  return handleResponse(
    await fetch(`/api/admin/support/pipelines/${name}/config`, {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify({ config }),
    })
  )
}

export async function fetchPipelineRuns(name: string): Promise<PipelineRun[]> {
  return handleResponse(
    await fetch(`/api/admin/support/pipelines/${name}/runs`, { headers: authHeaders() })
  )
}

// ─── Unanswered Questions ─────────────────────────────────────────────────────

export async function fetchUnansweredQuestions(params?: {
  is_resolved?: boolean
  page?: number
}): Promise<PagedResponse<UnansweredQuestion>> {
  const query = new URLSearchParams()
  if (params?.is_resolved !== undefined) query.set('is_resolved', String(params.is_resolved))
  if (params?.page) query.set('page', String(params.page))
  const qs = query.toString()
  return handleResponse(
    await fetch(`/api/admin/support/unanswered${qs ? `?${qs}` : ''}`, { headers: authHeaders() })
  )
}

export async function resolveUnansweredQuestion(
  id: string,
  notes?: string,
): Promise<UnansweredQuestion> {
  return handleResponse(
    await fetch(`/api/admin/support/unanswered/${id}/resolve`, {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify({ notes }),
    })
  )
}

// ─── Leads ────────────────────────────────────────────────────────────────────

export async function fetchLeads(params?: { page?: number }): Promise<PagedResponse<Lead>> {
  const query = new URLSearchParams()
  if (params?.page) query.set('page', String(params.page))
  const qs = query.toString()
  return handleResponse(
    await fetch(`/api/admin/support/leads${qs ? `?${qs}` : ''}`, { headers: authHeaders() })
  )
}

export async function triggerOktadesk(
  conversationId: string,
  subject?: string
): Promise<{ success: boolean; run_id: string; ticket_id?: string }> {
  return handleResponse(
    await fetch('/api/admin/support/pipelines/oktadesk/trigger', {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ conversation_id: conversationId, subject }),
    })
  )
}

// ─── Trainings (Treinamentos) ────────────────────────────────────────────────

export interface TrainingRequiredInput {
  name: string
  type: 'email' | 'text' | 'product_select'
  description: string
}

export interface Training {
  id: string
  name: string
  scenario: string
  instructions: string
  required_inputs: TrainingRequiredInput[]
  resolution_action: 'create_ticket' | 'respond'
  resolution_message: string | null
  is_enabled: boolean
  priority: number
  created_at: string
  updated_at: string
}

export async function fetchTrainings(): Promise<Training[]> {
  return handleResponse(
    await fetch('/api/admin/support/trainings', { headers: authHeaders() })
  )
}

export async function createTraining(data: {
  name: string
  scenario: string
  instructions: string
  required_inputs?: TrainingRequiredInput[]
  resolution_action?: string
  resolution_message?: string
  is_enabled?: boolean
  priority?: number
}): Promise<Training> {
  return handleResponse(
    await fetch('/api/admin/support/trainings', {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(data),
    })
  )
}

export async function updateTraining(
  id: string,
  data: Partial<{
    name: string
    scenario: string
    instructions: string
    required_inputs: TrainingRequiredInput[]
    resolution_action: string
    resolution_message: string
    is_enabled: boolean
    priority: number
  }>
): Promise<Training> {
  return handleResponse(
    await fetch(`/api/admin/support/trainings/${id}`, {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify(data),
    })
  )
}

export async function deleteTraining(id: string): Promise<void> {
  await handleResponse(
    await fetch(`/api/admin/support/trainings/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    })
  )
}

export async function toggleTraining(id: string): Promise<Training> {
  return handleResponse(
    await fetch(`/api/admin/support/trainings/${id}/toggle`, {
      method: 'PATCH',
      headers: authHeaders(),
    })
  )
}
